import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Pencil, Save, X, GripVertical, ArrowLeftRight } from "lucide-react";

export default function SmallNewsSlider() {
  const { selected } = useProvince();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const canEdit = user && ["super_admin", "province_admin", "editor"].includes(user?.role);

  const load = () => {
    // Use `sort=home_priority` so pinned items come first, then latest by date.
    const provincePart = selected ? `&province_id=${selected.id}` : "";
    api.get(`/news?limit=10&sort=home_priority${provincePart}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

  // ─── Reorder helpers ─────────────────────────────────────────────────
  const moveItem = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDirty(true);
  };
  const onDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverIdx(null);
    if (from == null) return;
    moveItem(from, idx);
  };
  const onDragEnd = () => {
    dragFromRef.current = null;
    setDragOverIdx(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const ids = items.map((it) => it.id);
      const r = await api.post("/news/reorder-home", { ids });
      toast.success(`Sıra kaydedildi (${r.data.updated}/${r.data.count})`);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sıra kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };
  const exitEdit = () => {
    if (dirty) {
      // reload from server to discard
      load();
    }
    setEditMode(false);
    setDirty(false);
  };

  if (!items.length) return null;

  return (
    <div data-testid="small-news-slider" className="w-full">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-6 bg-[#e11d2a] rounded-full"></div>
        <h3 className="text-lg font-black">SON HABERLER</h3>
        {canEdit && (
          <div className="ml-auto flex items-center gap-2" data-testid="small-slider-admin-controls">
            {!editMode ? (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                data-testid="small-slider-edit-toggle"
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-white border border-gray-200 hover:bg-gray-50 rounded-full shadow-sm text-gray-700"
              >
                <Pencil className="w-3 h-3" /> Sırala
              </button>
            ) : (
              <>
                <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 inline-flex items-center gap-1">
                  <ArrowLeftRight className="w-3 h-3" /> Kartları sürükleyin
                </span>
                {dirty && (
                  <button
                    type="button"
                    onClick={saveOrder}
                    disabled={saving}
                    data-testid="small-slider-save-order"
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-sm disabled:opacity-50"
                  >
                    <Save className="w-3 h-3" /> {saving ? "..." : "Kaydet"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={exitEdit}
                  data-testid="small-slider-edit-cancel"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-white border border-gray-200 hover:bg-gray-50 rounded-full shadow-sm text-gray-700"
                >
                  <X className="w-3 h-3" /> {dirty ? "İptal" : "Çıkış"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="h-slider flex gap-3 overflow-x-auto pb-2 no-scrollbar">
        {items.map((n, idx) => {
          const cardInner = (
            <>
              <div className="aspect-[16/10] overflow-hidden bg-gray-100 relative">
                {n.cover_image && <img src={n.cover_image} alt={n.title} className="w-full h-full object-cover" />}
                {editMode && (
                  <>
                    <span className="absolute top-1 left-1 text-[10px] font-bold bg-black/70 text-white rounded px-1.5 py-0.5">
                      {idx + 1}
                    </span>
                    <GripVertical className="absolute bottom-1 right-1 w-4 h-4 text-white drop-shadow" />
                  </>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  {n.category && <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: n.category.color }}>{n.category.name}</span>}
                  {n.province && <span className="text-[10px] text-gray-500">• {n.province.name}</span>}
                </div>
                <h4 className="text-sm font-semibold leading-snug line-clamp-2">{n.title}</h4>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{n.spot}</p>
              </div>
            </>
          );
          const dragProps = editMode ? {
            draggable: true,
            onDragStart: onDragStart(idx),
            onDragOver: onDragOver(idx),
            onDrop: onDrop(idx),
            onDragEnd,
            "data-testid": `small-slider-thumb-${idx}`,
          } : {};
          const cls = `news-card shrink-0 w-64 md:w-72 bg-white border rounded-lg overflow-hidden transition-all ${
            editMode
              ? `cursor-grab active:cursor-grabbing ${dragOverIdx === idx ? "border-amber-400 shadow-lg scale-[1.02]" : "border-[#e11d2a]/30"}`
              : "border-gray-100"
          }`;
          if (editMode) {
            // Prevent navigation while reordering
            return (
              <div key={n.id} {...dragProps} className={cls}>
                {cardInner}
              </div>
            );
          }
          return (
            <Link key={n.id} to={`/haber/${n.slug}`} className={cls}>
              {cardInner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
